import { Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import AcxButton from "components/UI/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { dateTimeColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { IAcxTableRow } from "components/UI/AcxTable/AcxTable";
import { Observer, observer } from "mobx-react";
import SoundClip from "models/SoundClip";
import { Moment } from "moment";
import React, { ChangeEvent } from "react";
import { useStore } from "utils/useStore";
import AcxDateRangeInput from "../../UI/Calendar/DateRange/AcxDateRangeInput";
import { MontageSoundClipStore } from "../Stores/MontageSoundClipStore";
import AcxMicroPlayer from "components/UI/Audio/AcxMicroPlayer";
import { KboGeneratedBadge } from "components/KBo/Branding/KboGeneratedBadge";

import { breadCrumbColumnType } from "components/UI/AcxDataGrid/ColumnTypes/AcxGridColTypes";
import {
    ChipComparator,
    TagFormatter,
} from "components/UI/AcxDataGrid/Formatters/TagFormatter";
import { Link, useNavigate } from "react-router-dom";
import { Routes } from "components/Navigation/Routes";
import { AuthStore } from "stores/AuthStore";

const SoundClipList = observer(() => {
    const store = useStore(MontageSoundClipStore);
    const orgStore = useStore(AuthStore).orgStore;
    const theme = useTheme();
    const navigate = useNavigate();

    React.useEffect(() => {
        function renderSelectedRows(row: IAcxTableRow) {
            const clip = row as SoundClip;
            return (
                <div key={row.id}>
                    <Grid
                        container
                        direction="row"
                        sx={{
                            paddingRight: theme.spacing(2),
                            paddingLeft: theme.spacing(2),
                            paddingTop: theme.spacing(1.3),
                            paddingBottom: theme.spacing(1.3),
                            minHeight: "45px",
                        }}
                        justifyContent={"space-between"}
                        wrap={"nowrap"}
                        alignItems={"center"}
                    >
                        <Grid
                            container
                            item
                            xs={11}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            spacing={1}
                        >
                            <Grid item>
                                <Typography
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        userSelect: "none",
                                        fontFamily: theme.typography.fontFamily,
                                    }}
                                >
                                    {clip.evaluationQbId && (
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                userSelect: "none",
                                                fontFamily:
                                                    theme.typography.fontFamily,
                                                fontWeight: theme.typography
                                                    .fontWeightBold as any,
                                            }}
                                        >
                                            Evaluation Id:{" "}
                                        </span>
                                    )}
                                    {clip.evaluationQbId
                                        ? clip.evaluationQbId + " "
                                        : ""}
                                    {clip.segmentName && (
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                userSelect: "none",
                                                fontFamily:
                                                    theme.typography.fontFamily,
                                                fontWeight: theme.typography
                                                    .fontWeightBold as any,
                                            }}
                                        >
                                            Clip Name:{" "}
                                        </span>
                                    )}
                                    {clip.segmentName ? clip.segmentName : ""}
                                </Typography>
                            </Grid>

                            <Grid item xs>
                                {clip.tags}
                            </Grid>

                            {clip.note && (
                                <Grid item xs={4}>
                                    <Typography
                                        sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            userSelect: "none",
                                            fontFamily:
                                                theme.typography.fontFamily,
                                            fontWeight: theme.typography
                                                .fontWeightBold as any,
                                        }}
                                    >
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                userSelect: "none",
                                                fontFamily:
                                                    theme.typography.fontFamily,
                                                fontWeight: theme.typography
                                                    .fontWeightBold as any,
                                            }}
                                        >
                                            Note:
                                        </span>{" "}
                                        {clip.note}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>

                        <Grid item xs={1} container justifyContent={"flex-end"}>
                            <IconButton
                                size="small"
                                sx={{ color: theme.palette.action.disabled }}
                                onClick={() =>
                                    store.soundClipDgStore.removeSelectedByIdWithVisibilityOverride(
                                        clip.id,
                                    )
                                }
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </Grid>
                    </Grid>
                </div>
            );
        }

        const montageReset = (
            <Tooltip title="Clear Montage">
                <IconButton
                    onClick={store.resetAll}
                    style={{
                        marginBottom: "-0.5rem",
                    }}
                    size="large"
                >
                    <ClearAllIcon />
                </IconButton>
            </Tooltip>
        );

        const dateTimeSelector = (
            <Observer>
                {() => (
                    <AcxDateRangeInput
                        labelText={"Select Created On Date Range"}
                        defaultStartDate={store.datePickerStore.beginDate}
                        defaultEndDate={store.datePickerStore.endDate}
                        onSelect={(start: Moment, end: Moment) => {
                            store.datePickerStore.setBeginDate(start);
                            store.datePickerStore.setEndDate(end);
                        }}
                    />
                )}
            </Observer>
        );

        const qbEvalIdSearch = (
            <AcxMainTextField
                key="montage-search-qbevalid"
                labelText="Find Conversation"
                id="montage-search-qbevalid"
                value=""
                placeholderText="Eval Number or Conversation ID"
                onChange={(evt: ChangeEvent<HTMLInputElement>) =>
                    (store.qbEvalIdSearch = evt.currentTarget.value)
                }
                labelSx={{ fontSize: "13px" }}
                containerStyle={{ width: "260px" }}
            />
        );

        const createButton = (
            <Observer>
                {() => (
                    <AcxButton
                        fullWidth
                        key="start-montage"
                        style={{ marginRight: "auto" }}
                        onClick={store.onStartBuildMontage}
                        color="primary"
                        disabled={
                            store.soundClipDgStore.selectedRowIds.length <= 0
                        }
                    >
                        Create Montage
                    </AcxButton>
                )}
            </Observer>
        );

        const columns: IColDef[] = [
            {
                headerName: "Conversation",
                field: "audioMetadataId",
                filterable: false,
                sortable: false,
                flex: 1,

                renderCell: (params) => {
                    const conversationId = params.row.audioMetadataId;

                    return (
                        <Grid
                            container
                            item
                            xs={12}
                            alignItems={"center"}
                            height="100%"
                        >
                            <AcxButton
                                color="white"
                                component="a"
                                onClick={() => {
                                    navigate(
                                        Routes.makeConversationsRoute(
                                            conversationId ?? "",
                                        ),
                                    );
                                }}
                                style={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                }}
                            >
                                View
                            </AcxButton>
                        </Grid>
                    );
                },
            },
            {
                headerName: "Conversation Id",
                field: "conversationId",
                valueGetter: (_, row) => row.audioMetadataId,
                flex: 1,
            },
            {
                headerName: "Eval Number",
                field: "evaluationQbId",
                flex: 1,
                renderCell: (params) => {
                    const orgId = orgStore.selectedOrganization?.id;
                    const evalId = params.row.evaluationId;

                    return (
                        <Link
                            className="pendo-ignore"
                            data-testid="sound-clip-dg-evalulation-number-link"
                            to={Routes.makeEvaluationRoute(
                                orgId ?? "",
                                evalId ?? "",
                            )}
                        >
                            {params.row.evaluationQbId}
                        </Link>
                    );
                },
            },
            {
                field: "id",
                headerName: " ",
                disableColumnMenu: true,
                hideSortIcons: true,
                filterable: false,
                width: 80,
                align: "center",
                renderCell: ({ value }) => {
                    if (value === undefined) {
                        return <></>;
                    }

                    return (
                        <AcxMicroPlayer
                            style={{ marginLeft: "auto", marginRight: "auto" }}
                            downloadMediaFileId={value}
                            mediaSourceType="soundClip"
                        />
                    );
                },
            },
            {
                headerName: "Clip Name",
                field: "segmentName",
                flex: 1,
            },
            {
                headerName: "Owner",
                field: "createdBy",
                flex: 1,
                renderCell: (params) => (
                    <Grid
                        container
                        alignItems="center"
                        gap={1}
                        wrap="nowrap"
                        height="100%"
                    >
                        <Typography fontSize="inherit" noWrap>
                            {params.value}
                        </Typography>
                        {params.row.isKboGenerated && <KboGeneratedBadge />}
                    </Grid>
                ),
            },
            {
                headerName: "Created Date",
                field: "createdOn",
                flex: 1,
                ...dateTimeColumnType,
            },
            {
                headerName: "Interaction Date",
                field: "interactionDate",
                flex: 1,
                ...dateTimeColumnType,
            },
            {
                headerName: "Agent Name",
                field: "agentName",
                flex: 1,
            },
            {
                headerName: "Customer Type",
                field: "customerType",
                flex: 1,
            },
            {
                headerName: "Call Direction",
                field: "callDirection",
                flex: 1,
            },
            {
                headerName: "Service Hierarchy",
                field: "serviceHierarchy",
                width: 300,
                ...breadCrumbColumnType,
                valueGetter: (_, row, column) => {
                    return row.serviceHierarchy?.split(">");
                },
            },
            {
                headerName: "Tags",
                field: "tags",
                width: 150,
                renderCell: TagFormatter(),
                sortComparator: ChipComparator,
            },
            {
                headerName: "Notes",
                field: "note",

                flex: 1,
            },
            {
                headerName: "Transcription Text",
                field: "transcriptionText",

                flex: 1,
            },
        ];

        store.soundClipDgStore.setColumns(columns);

        store.soundClipDgStore.controls = [
            qbEvalIdSearch,
            dateTimeSelector,
            montageReset,
        ];

        store.soundClipDgStore.selectionContextOptions = {
            itemName: "Sound Clip",
            itemNamePluralized: "Sound Clips",
            action: createButton,
            renderSelectedRow: renderSelectedRows,
        };

        store.soundClipDgStore.controlsColumnSpan = 12;
        store.soundClipDgStore.controlsColumnStyle = { marginLeft: "0px" };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        store.datePickerStore.beginDate,
        store.datePickerStore.endDate,
        store.qbEvalIdSearch,
        store.soundClipDgStore,
    ]);

    return (
        <Observer>
            {() => (
                <div
                    style={{
                        height: "90dvh",
                        paddingBottom: "5rem",
                        overflow: "hidden",
                    }}
                >
                    {store.orgId ? (
                        <AcxDataGrid dataGridStore={store.soundClipDgStore} />
                    ) : null}
                </div>
            )}
        </Observer>
    );
});

export default SoundClipList;
